import * as React from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownList,
  Flex,
  Icon,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  SearchInput,
  Select,
  SelectOption,
  Content,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Pagination,
} from '@patternfly/react-core';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { StatusLabel } from '@app/components/StatusLabel';
import { TruncatedLabel } from '@app/components/TruncatedLabel';
import { FLEETS, FLEET_STATUS_TONE, type FleetStatus } from '@app/data/fleetData';
import {
  CheckCircleIcon,
  EllipsisVIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  TimesCircleIcon,
} from '@patternfly/react-icons';

const Fleets: React.FunctionComponent = () => {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('All fleets');
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedFleets, setSelectedFleets] = React.useState<Set<string>>(new Set());
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});

  // Pagination state
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);

  const getStatusLabel = (status: FleetStatus) => (
    <StatusLabel status={FLEET_STATUS_TONE[status]} pulse={status === 'Running'}>
      {status}
    </StatusLabel>
  );

  const handleSelectFleet = (fleetId: string, isSelected: boolean) => {
    setSelectedFleets(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(fleetId);
      } else {
        newSet.delete(fleetId);
      }
      return newSet;
    });
  };

  const onActionDropdownToggle = (fleetId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [fleetId]: !prev[fleetId]
    }));
  };

  const onActionDropdownSelect = (fleetId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [fleetId]: false
    }));
  };

  const onFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const onFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedFilter(value as string);
    setIsFilterOpen(false);
  };

  const filterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onFilterToggle}
      isExpanded={isFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedFilter}
    </MenuToggle>
  );

  // Filter fleets based on search
  const filteredFleets = React.useMemo(() => {
    const query = searchValue.toLowerCase();
    return FLEETS.filter((fleet) => {
      const matchesFilter = selectedFilter === 'All fleets' || fleet.status === selectedFilter;
      const matchesSearch =
        !query ||
        fleet.name.toLowerCase().includes(query) ||
        fleet.systemImage.toLowerCase().includes(query);
      return matchesFilter && matchesSearch;
    });
  }, [searchValue, selectedFilter]);

  // Pagination logic
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedFleets = filteredFleets.slice(startIndex, endIndex);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1);
  }, [searchValue, selectedFilter]);

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedFleets(new Set(paginatedFleets.map(f => f.id)));
    } else {
      setSelectedFleets(new Set());
    }
  };

  const isAllSelected = selectedFleets.size === paginatedFleets.length && paginatedFleets.length > 0;
  const isPartiallySelected = selectedFleets.size > 0 && selectedFleets.size < paginatedFleets.length;

  return (
    <>
      {/* Page Header with Toolbar - Single Line */}
      <PageSection hasBodyWrapper={false} className="acm-page__header-section">
        <div className="acm-page__header-inner">
        <Flex 
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
        >
          <PageHeaderTitle
            title="Fleets"
            description="Organize devices into fleets and manage device fleet configurations."
          />
          <PageContextActions placement="toolbar" />
        </Flex>
        </div>
      </PageSection>

      {/* Main Content — white panel: filters, search, CTAs, table, pagination */}
      <PageSection hasBodyWrapper={false} className="acm-page__content-section">
        <div className="acm-page__panel">
          <div className="acm-page__panel-toolbar">
            <Toolbar>
              <ToolbarContent>
                {selectedFleets.size > 0 ? (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                    <ToolbarItem>
                      <Content component="p" style={{ margin: 0, marginRight: 'var(--pf-t--global--spacer--md)' }}>
                        {selectedFleets.size} selected
                      </Content>
                    </ToolbarItem>
                    <ToolbarItem>
                      <Button variant="danger" onClick={() => setSelectedFleets(new Set())}>
                        Delete
                      </Button>
                    </ToolbarItem>
                  </ToolbarGroup>
                ) : (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                      <ToolbarItem>
                        <Select
                          toggle={filterToggle}
                          onSelect={onFilterSelect}
                          selected={selectedFilter}
                          isOpen={isFilterOpen}
                          onOpenChange={(isOpen) => setIsFilterOpen(isOpen)}
                        >
                          <SelectOption value="All fleets">All fleets</SelectOption>
                          <SelectOption value="Successful">Successful</SelectOption>
                          <SelectOption value="Warning">Warning</SelectOption>
                          <SelectOption value="Running">Running</SelectOption>
                          <SelectOption value="Failed">Failed</SelectOption>
                        </Select>
                      </ToolbarItem>
                      <ToolbarItem>
                        <SearchInput
                          className="app-search-input"
                          placeholder="Search"
                          value={searchValue}
                          onChange={(_, value) => setSearchValue(value)}
                          onClear={() => setSearchValue('')}
                        />
                      </ToolbarItem>
                      <ToolbarItem>
                        <Button variant="primary">
                          Create fleets
                        </Button>
                      </ToolbarItem>
                      <ToolbarItem>
                        <Button variant="secondary">
                          Import fleets
                        </Button>
                      </ToolbarItem>
                    </ToolbarGroup>
                )}
              </ToolbarContent>
            </Toolbar>
          </div>

            {/* Fleets Table */}
            <div className="app-data-table">
              {/* Table Header */}
              <div className="app-data-table__header app-data-table__grid app-data-table__grid--fleets">
                <div>
                  <Checkbox
                    id="select-all-fleets"
                    isChecked={isAllSelected ? true : isPartiallySelected ? null : false}
                    onChange={(_, checked) => handleSelectAll(checked)}
                    aria-label="Select all fleets"
                  />
                </div>
                <div>Name</div>
                <div>System image</div>
                <div>Devices</div>
                <div>Status</div>
                <div></div>
              </div>

              {/* Table Rows */}
              {paginatedFleets.map((fleet, index) => {
                return (
                  <div
                    key={fleet.id}
                    className="app-data-table__row app-data-table__grid app-data-table__grid--fleets"
                    style={{
                        borderBottom: index < paginatedFleets.length - 1
                        ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                        : 'none',
                    }}
                  >
                    <div className="app-data-table__control app-data-table__control--select">
                      <Checkbox
                        id={`select-fleet-${fleet.id}`}
                        isChecked={selectedFleets.has(fleet.id)}
                        onChange={(_, checked) => handleSelectFleet(fleet.id, checked)}
                        aria-label={`Select ${fleet.name}`}
                      />
                    </div>
                    <div data-label="Name">
                      <a
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        style={{
                          color: 'var(--pf-t--global--color--brand--default)',
                          textDecoration: 'none',
                        }}
                      >
                        <TruncatedLabel content={fleet.name} />
                      </a>
                    </div>
                    <div data-label="System image">
                      <TruncatedLabel content={fleet.systemImage} />
                    </div>
                    <div data-label="Devices">{fleet.deviceCount.toLocaleString()}</div>
                    <div data-label="Status">
                      {getStatusLabel(fleet.status)}
                    </div>
                    <div className="app-data-table__control app-data-table__control--actions">
                      <Dropdown
                        isOpen={openDropdowns[fleet.id] || false}
                        onSelect={() => onActionDropdownSelect(fleet.id)}
                        onOpenChange={(isOpen: boolean) => {
                          setOpenDropdowns(prev => ({
                            ...prev,
                            [fleet.id]: isOpen
                          }));
                        }}
                        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                          <MenuToggle
                            ref={toggleRef}
                            onClick={() => onActionDropdownToggle(fleet.id)}
                            isExpanded={openDropdowns[fleet.id] || false}
                            variant="plain"
                            icon={<EllipsisVIcon />}
                            aria-label={`Actions for ${fleet.name}`}
                          />
                        )}
                      >
                        <DropdownList>
                          <DropdownItem
                            key="view-details"
                            onClick={() => onActionDropdownSelect(fleet.id)}
                          >
                            View
                          </DropdownItem>
                          <DropdownItem
                            key="edit"
                            onClick={() => onActionDropdownSelect(fleet.id)}
                          >
                            Edit
                          </DropdownItem>
                          <DropdownItem
                            key="delete"
                            onClick={() => onActionDropdownSelect(fleet.id)}
                          >
                            Delete
                          </DropdownItem>
                        </DropdownList>
                      </Dropdown>
                    </div>
                  </div>
                );
              })}
            </div>

          <div className="acm-page__panel-footer">
              <Pagination
                itemCount={filteredFleets.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                widgetId="fleets-pagination"
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
          </div>
        </div>
    </PageSection>
    </>
  );
};

export { Fleets };

